import { useCallback, useEffect, useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import type { DecisionMakerMatch, LinkableDecisionMakerHitData } from '#src/app/pages/search/shared/api';
import { useSearchAssetUrl, useSearchResults, useSearchState } from '#src/app/pages/search/shared/hooks';
import { useCallOnce } from '#src/app/shared/hooks/tracking';

type DecisionMakerCardClickTarget = 'name' | 'view_profile';

export type DecisionMakerCardData = {
	id: string;
	name: string;
	title: string | undefined;
	avatarUrl: string | undefined;
	profileUrl: string;
	verified: boolean;
	petitionsCount: number;
	petitionsCountLocalized: string;
};

type Result = ModelHookResult<
	{ decisionMakers: DecisionMakerCardData[] },
	{ trackClick: (decisionMakerId: string, target: DecisionMakerCardClickTarget) => void }
>;

type CardTrackingData = Required<
	Pick<
		TrackingProperties,
		| 'dm_id'
		| 'dm_verified'
		| 'dm_petitions_num'
		| 'q'
		| 'total_count'
		| 'match_type'
		| 'card_variant'
		| 'card_count'
		| 'card_position'
	>
>;

function normalizeQuery(query: string): string {
	return query.trim().toLowerCase();
}

export function useDecisionMakerCard(): Result {
	const { decisionMaker: match, nbHits, query, isLoading, isError } = useSearchResults();
	const { query: currentQuery, page } = useSearchState();
	const { localizeNumber } = useI18n();
	const getAssetUrl = useSearchAssetUrl();
	const track = useTracking();

	// while a new query or page loads, the previous results (and their match) are still around: only show a
	// match that belongs to what's on screen now, so the cards don't linger above another query's results
	const isOnScreen = !isError && page === 0 && normalizeQuery(query) === normalizeQuery(currentQuery);
	const visibleMatch: DecisionMakerMatch | null = isOnScreen ? match : null;

	const shown = useMemo(() => visibleMatch?.decisionMakers ?? [], [visibleMatch]);

	const trackingData = useMemo((): Map<string, CardTrackingData> => {
		if (!visibleMatch) return new Map();
		const { decisionMakers, matchType } = visibleMatch;
		return new Map(
			decisionMakers.map((decisionMaker: LinkableDecisionMakerHitData, index): [string, CardTrackingData] => [
				decisionMaker.id,
				{
					dm_id: `dm_${decisionMaker.id}`,
					dm_verified: !!decisionMaker.verified_at,
					dm_petitions_num: decisionMaker.petition_count,
					q: query,
					total_count: nbHits,
					match_type: matchType,
					card_variant: decisionMakers.length > 1 ? 'compact_card' : 'profile_card',
					card_count: decisionMakers.length,
					card_position: index + 1,
				},
			]),
		);
	}, [nbHits, query, visibleMatch]);

	const trackViews = useCallback(async () => {
		await Promise.all([...trackingData.values()].map(async (data) => track('search_dm_card_view', data)));
		return true;
	}, [track, trackingData]);
	// one view per decision maker per query, even as other filters change the results. The deps are the query and
	// the cards shown (joined, so there's a fixed number of them)
	const trackViewsOnce = useCallOnce(trackViews, [query, [...trackingData.keys()].join(',')]);

	useEffect(() => {
		// while fetching, the previous results are still shown. Past the first page or on an error, the cards are only
		// hidden, so the same query's views shouldn't be tracked again when they come back. An error also leaves the
		// last cards remembered, so "kelly" → (a query that fails) → "kelly" doesn't track again: rare, and only undercounts
		if (isLoading || !isOnScreen) return;
		// also called for a query without cards (tracking nothing), so coming back to an earlier query tracks its views
		// again: "kelly" → "climate" → "kelly"
		void trackViewsOnce();
	}, [isLoading, isOnScreen, trackViewsOnce]);

	const trackClick = useCallback(
		(decisionMakerId: string, target: DecisionMakerCardClickTarget) => {
			const data = trackingData.get(decisionMakerId);
			if (!data) return;
			void track('search_dm_card_click', { ...data, target });
		},
		[track, trackingData],
	);

	return {
		data: {
			decisionMakers: useMemo(
				() =>
					shown.map((decisionMaker) => ({
						id: decisionMaker.id,
						name: decisionMaker.name,
						title: decisionMaker.title,
						avatarUrl: getAssetUrl(decisionMaker.photo_url),
						profileUrl: `/decision-makers/${encodeURIComponent(decisionMaker.slug)}?source_location=search_dm_card`,
						verified: !!decisionMaker.verified_at,
						petitionsCount: decisionMaker.petition_count,
						petitionsCountLocalized: localizeNumber(decisionMaker.petition_count),
					})),
				[getAssetUrl, localizeNumber, shown],
			),
		},
		actions: { trackClick },
	};
}
